<script setup lang="ts">
  import { Input } from '@/components/ui/input';
  import { Label } from '@/components/ui/label';
  import { Textarea } from '@/components/ui/textarea';
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
  import LoadingWheel from '../components/Loading.vue';
  import AxiosErrorHandler from '../components/AxiosErrorHandler.vue';
  import SectionTitle from '../components/SectionTitle.vue';
  import { computed, onMounted, reactive, ref, watch } from 'vue';
  import { ApiKey } from '../api/symbols.ts';
  import { injectStrict } from '../api/injection.ts';
  import { useRoute, useRouter } from 'vue-router';
  import InputLabel from '../components/forms/InputLabel.vue';
  import SideHeader from '../components/SideHeader.vue';
  import { withCatch } from '@/utils/await-safe.ts';
  import { Button } from '@/components/ui/button';
  import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
  import * as Sentry from '@sentry/vue';

  const route = useRoute();
  const router = useRouter();
  const api = injectStrict(ApiKey);

  const loading = ref<boolean>(false);
  let errors = ref<{}>({});

  const id = ref<string>();
  const token = ref<string>();

  const vaName = ref('Jon Snow');
  const evaluatorName = ref<string>('');
  const position = ref<string>('');
  const clientName = ref('');
  const clientType = ref<string>();
  const anniversaryDate = ref<string>();
  const desiredIncrease = ref<string>('4');
  const clientRate = ref<number>(0);
  const generalVAComments = ref<string>();
  const experienceWithMOD = ref<number>(4);
  const companyFollowUpRating = ref<number>(4);
  const effectivenessOfVA = ref<string>('90-100%');
  const VASavings = ref<string>('91% - 100%');
  const knowSomeoneInRealEstate = ref<string>('1 - 5');
  const wouldRecommend = ref<'Yes' | 'No'>('Yes');
  const commentsAboutMOD = ref<string>();
  const approvedSalaryIncrease = ref<string>();
  const effectiveFrom = ref<string>();
  const monthlyHoursWorked = ref<number>(1);
  const effectiveDate = ref<string>();

  interface Scores {
    accurately: number;
    adaptability: number;
    completesNecessaryWork: number;
    compliesWithWorkingHours: number;
    communicationSkills: number;
    consistently: number;
    decisionMaking: number;
    effectively: number;
    exhibitsLoyalty: number;
    followsProcedure: number;
    handlesJobSpecifications: number;
    initiatesAction: number;
    isDiscreteWithActions: number;
    meetsDeadlines: number;
    observesPersonalPolicies: number;
    prioritizesTasks: number;
    reasoning: number;
    reliability: number;
    selfStarter: number;
    showsOriginality: number;
    thoroughly: number;
    worksWellWithAdmins: number;
    worksWithOthers: number;
  }

  const scores = reactive<Scores>({
    accurately: 4, //literally no idea what this field even meant, so just set it to excellent
    adaptability: 4,
    completesNecessaryWork: 4,
    compliesWithWorkingHours: 4,
    communicationSkills: 4,
    consistently: 4, //literally no idea what this field even meant, so just set it to excellent
    decisionMaking: 4,
    effectively: 4, //literally no idea what this field even meant, so just set it to excellent
    exhibitsLoyalty: 4,
    followsProcedure: 4,
    handlesJobSpecifications: 4,
    initiatesAction: 4,
    isDiscreteWithActions: 4,
    meetsDeadlines: 4,
    observesPersonalPolicies: 4,
    prioritizesTasks: 4,
    reasoning: 4,
    reliability: 4,
    selfStarter: 4,
    showsOriginality: 4,
    thoroughly: 4, //literally no idea what this field even meant so just set it to excellent
    worksWellWithAdmins: 4,
    worksWithOthers: 4,
  });

  const effectiveDates = ["Pro-rated to virtual assistant's anniversary date ( this option is usually chosen if annual performance review is completed after anniversary date).", 'Next invoice (Billing cycle starts on the 11th and 26th of the month).'];
  const qualities = [
    { label: 'Excellent', value: 4 },
    { label: 'Good', value: 3 },
    { label: 'Fair', value: 2 },
    { label: 'Poor', value: 1 },
  ];
  const effectivenessLevels = ['90-100%', '80-90%', '70%'];
  const effectivenessLevels2 = ['91% - 100%', '41% - 90%', '31% - 40%', '21% - 30%', '10% - 20%'];
  const knownFriends = ['11 or more', '5 - 10', '1 - 5', 'I dont know anyone'];
  const yesNo = ['Yes', 'No'];

  const newMonthlyRate = computed(() => {
    return (clientRate.value + clientRate.value * (parseFloat(desiredIncrease.value) / 100)).toFixed(2);
  });

  const vaHourlyIncrease = computed(() => {
    let updatedRate = clientRate.value + clientRate.value * (parseFloat(desiredIncrease.value) / 100);
    return (((updatedRate - clientRate.value) * 0.9) / monthlyHoursWorked.value).toFixed(2);
  });

  const score = computed((): string => {
    let value = 0;
    let count = 0;
    Object.keys(scores).forEach((key: keyof Scores) => {
      if (scores[key]) {
        value += parseInt(scores[key] as unknown as string);
        count++;
      }
    });
    if (count === 0) {
      return '0.00';
    }
    return (value / count).toFixed(2);
  });

  const bestScore = computed(() => {
    let roundedScore = Math.round(parseFloat(score.value));
    switch (roundedScore) {
      case 1:
        return `Poor - ${score.value}`;
      case 2:
        return `Fair - ${score.value}`;
      case 3:
        return `Good - ${score.value}`;
      case 4:
        return `Excellent -  ${score.value}`;
      default:
        return 'Unknown - 0';
    }
  });

  watch(desiredIncrease, (newValue) => {
    if (newValue && parseFloat(newValue) < 4) {
      desiredIncrease.value = '4';
    } else {
      desiredIncrease.value = '' + Math.ceil(parseFloat(newValue));
    }
  });

  function loadCredentials() {
    if (!route.query.id || !route.query.token) {
      router.push('/error');
      return;
    }
    id.value = route.query.id as string | undefined;
    token.value = route.query.token as string | undefined;
  }

  async function resolve() {
    const config = { params: { id: id.value, token: token.value } };
    loading.value = true;
    const [error, resp] = await withCatch(api.get('client/ape/resolve', config));
    loading.value = false;
    if (error || !resp) {
      await router.push('/error');
      return;
    }
    transformDataFromAPI(resp.data as APEResponse);
  }

  interface APEResponse {
    VA__c: string;
    client__c: string;
    Account_Invoice_Type__c: string;
    position__c: string;
    evaluator__c: string;
    gencomm__c: string;
    expwithmod__c: string;
    commfolloup__c: string;
    effofVA__c: string;
    VAmonsav__c: string;
    knowVAinRB__c: string;
    recommend__c: 'Yes' | 'No';
    modgencomm__c: string;
    approvedincr__c: string;
    eff_from__c: string;
    effectiveDates: string;
    Accrly__c: string;
    adapt__c: string;
    commskills__c: string;
    compnecwork__c: string;
    compesthrs__c: string;
    constly__c: string;
    makesdec__c: string;
    effectly__c: string;
    exsenloy__c: string;
    accfolproc__c: string;
    knowledge__c: string;
    initact__c: string;
    dissa__c: string;
    meetdl__c: string;
    perspol__c: string;
    prtytask__c: string;
    soundreason__c: string;
    honsrel__c: string;
    selfstrtr__c: string;
    showorig__c: string;
    thorougly__c: string;
    workadmin__c: string;
    hr__c: string;
    VA_Anniversary_Date__c: string;
    Fixed_Monthly_Hours1__c: string;
    Client_s_Current_Monthly_Rate__c: string;
  }

  function transformDataFromAPI(payload: APEResponse) {
    vaName.value = payload.VA__c;
    clientName.value = payload.client__c;
    clientType.value = payload.Account_Invoice_Type__c;
    position.value = payload.position__c;
    evaluatorName.value = payload.evaluator__c;
    generalVAComments.value = payload.gencomm__c;
    experienceWithMOD.value = parseInt(payload.expwithmod__c);
    companyFollowUpRating.value = parseInt(payload.commfolloup__c);
    effectivenessOfVA.value = payload.effofVA__c;
    VASavings.value = payload.VAmonsav__c;
    knowSomeoneInRealEstate.value = payload.knowVAinRB__c;
    wouldRecommend.value = payload.recommend__c;
    commentsAboutMOD.value = payload.modgencomm__c;
    approvedSalaryIncrease.value = payload.approvedincr__c;
    effectiveFrom.value = payload.eff_from__c;
    effectiveDate.value = payload.effectiveDates;

    //scores
    scores.accurately = parseInt(payload.Accrly__c);
    scores.adaptability = parseInt(payload.adapt__c);
    scores.communicationSkills = parseInt(payload.commskills__c);
    scores.completesNecessaryWork = parseInt(payload.compnecwork__c);
    scores.compliesWithWorkingHours = parseInt(payload.compesthrs__c);
    scores.consistently = parseInt(payload.constly__c);
    scores.decisionMaking = parseInt(payload.makesdec__c);
    scores.effectively = parseInt(payload.effectly__c);
    scores.exhibitsLoyalty = parseInt(payload.exsenloy__c);
    scores.followsProcedure = parseInt(payload.accfolproc__c);
    scores.handlesJobSpecifications = parseInt(payload.knowledge__c);
    scores.initiatesAction = parseInt(payload.initact__c);
    scores.isDiscreteWithActions = parseInt(payload.dissa__c);
    scores.meetsDeadlines = parseInt(payload.meetdl__c);
    scores.observesPersonalPolicies = parseInt(payload.perspol__c);
    scores.prioritizesTasks = parseInt(payload.prtytask__c);
    scores.reasoning = parseInt(payload.soundreason__c);
    scores.reliability = parseInt(payload.honsrel__c);
    scores.selfStarter = parseInt(payload.selfstrtr__c);
    scores.showsOriginality = parseInt(payload.showorig__c);
    scores.thoroughly = parseInt(payload.thorougly__c);
    scores.worksWellWithAdmins = parseInt(payload.workadmin__c);
    scores.worksWithOthers = parseInt(payload.hr__c);

    anniversaryDate.value = payload.VA_Anniversary_Date__c;
    desiredIncrease.value = '4';
    monthlyHoursWorked.value = parseInt(payload.Fixed_Monthly_Hours1__c);
    clientRate.value = parseInt(payload.Client_s_Current_Monthly_Rate__c);
  }

  const transformForApi = () => {
    return {
      id: id.value,
      token: token.value,

      accfolproc__c: scores.followsProcedure,
      Accrly__c: scores.accurately,
      adapt__c: scores.adaptability,

      client__c: clientName.value,
      commfolloup__c: companyFollowUpRating.value,
      commskills__c: scores.communicationSkills,
      compesthrs__c: scores.compliesWithWorkingHours,
      compnecwork__c: scores.completesNecessaryWork,
      constly__c: scores.consistently,
      dissa__c: scores.isDiscreteWithActions,
      effectiveDates: effectiveDate.value,
      effectly__c: scores.effectively,
      effofVA__c: effectivenessOfVA.value,
      evaluator__c: evaluatorName.value,
      expwithmod__c: experienceWithMOD.value,
      exsenloy__c: scores.exhibitsLoyalty,
      gencomm__c: generalVAComments.value,
      honsrel__c: scores.reliability,
      hr__c: scores.worksWithOthers,
      initact__c: scores.initiatesAction,
      knowledge__c: scores.handlesJobSpecifications,
      knowVAinRB__c: knowSomeoneInRealEstate.value,
      makesdec__c: scores.decisionMaking,
      meetdl__c: scores.meetsDeadlines,
      modgencomm__c: commentsAboutMOD.value,
      perspol__c: scores.observesPersonalPolicies,
      position__c: position.value,
      prtytask__c: scores.prioritizesTasks,
      recommend__c: wouldRecommend.value,
      selfstrtr__c: scores.selfStarter,
      showorig__c: scores.showsOriginality,
      soundreason__c: scores.reasoning,
      thorougly__c: scores.thoroughly,
      VA__c: vaName.value,
      VAmonsav__c: VASavings.value,
      workadmin__c: scores.worksWellWithAdmins,

      Score__c: score.value, //sf onl excepts 2 decimals
      vafinalratings__c: score.value, // sf only excepts 2 decimals,
      effdate__c: effectiveDate.value,
      eff_from__c: effectiveFrom.value,

      rateincrx__c: approvedSalaryIncrease.value,
      approvedincr__c: approvedSalaryIncrease.value,
      Desired_Percentage__c: desiredIncrease.value,
    };
  };

  const submit = async () => {
    errors.value = {};
    loading.value = true;
    const [error, resp] = await withCatch(api.post('client/ape/submit', transformForApi()));
    loading.value = false;

    if (error || !resp) {
      // The submit may have persisted server-side while the browser still
      // saw a non-2xx / network error. Capture the real error (withCatch
      // otherwise swallows it before Sentry sees it) and never fail silently.
      Sentry.captureException(error, {
        extra: {
          endpoint: 'client/ape/submit',
          status: error?.response?.status,
          data: error?.response?.data,
        },
      });

      const data = error?.response?.data;
      const fieldErrors = data?.errors;
      const hasFieldErrors =
        fieldErrors &&
        (Array.isArray(fieldErrors) ? fieldErrors.length > 0 : Object.keys(fieldErrors).length > 0);

      // Always end with something renderable -- never a blank {} -- so the
      // user is never left staring at a page that silently did nothing.
      errors.value = hasFieldErrors
        ? fieldErrors
        : data?.message
          ? [data.message]
          : ['Something went wrong submitting your evaluation. Please try again, or contact your MyOutDesk representative if this keeps happening.'];
      window.scrollTo(0, 0);
      return;
    }

    await router.push('/success');
  };

  onMounted(async () => {
    loadCredentials();
    await resolve();
  });
</script>

<template>
  <div class="mt-6">
    <LoadingWheel v-if="loading" />
    <div v-else>
      <div v-if="Object.keys(errors).length > 0" class="p-6">
        <AxiosErrorHandler :errors="errors" />
      </div>
      <SectionTitle>
        <template #default>Annual Performance Evaluation </template>
        <template #subtitle>Reflecting on Growth, Achievements, and Opportunities for Continued Success</template>
      </SectionTitle>
      <div class="space-y-8 md:space-y-16">
        <!--Account Information-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Account Information</template>
            <template #subtitle>Documenting evaluator details for the annual performance review.</template>
          </SideHeader>
          <div class="border-gray-200 md:col-span-2">
            <div class="grid grid-cols-2 md:grid-cols-2 gap-4">
              <div class="space-y-2">
                <Label>Virtual Professional Name</Label>
                <Input v-model="vaName" aria-label="va-name" disabled />
              </div>
              <div class="space-y-2">
                <Label>Client Company Name</Label>
                <Input v-model="clientName" aria-label="client company name" disabled />
              </div>
              <div class="space-y-2">
                <Label>Position</Label>
                <Input v-model="position" aria-label="Position in Company" />
              </div>
              <div class="space-y-2">
                <Label>Evaluator Name</Label>
                <Input v-model="evaluatorName" aria-label="Evaluator Name" />
              </div>
            </div>
          </div>
        </div>

        <!--Quality Assessment-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Quality Assessment</template>
            <template #subtitle>Evaluating the accuracy, consistency, and excellence of work outputs.</template>
          </SideHeader>

          <div class="md:col-span-2 space-y-4">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to complete the required workload?
            </Label>
            <RadioGroup v-model="scores.completesNecessaryWork" :default-value="4">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-cnw-` + option" :value="option.value" />
                <Label :for="index + `-cnw-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to prioritize tasks and manage time effectively?
            </Label>
            <RadioGroup v-model="scores.prioritizesTasks">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-pt-` + option" :value="option.value" />
                <Label :for="index + `-pt-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Knowledge-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Knowledge</template>
            <template #subtitle>Assessing expertise, skills, and understanding relevant to the role.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-4">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> capability to manage all phases and details within job specifications?
            </Label>
            <RadioGroup v-model="scores.handlesJobSpecifications">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-hjs-` + option" :value="option.value" />
                <Label :for="index + `-hjs-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Human Relations-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Human Relations</template>
            <template #subtitle>Evaluating interpersonal skills, teamwork, and relationship-building abilities.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-4">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> willingness to collaborate with others?
            </Label>
            <RadioGroup v-model="scores.worksWithOthers">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-wwo-` + option" :value="option.value" />
                <Label :for="index + `-wwo-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Attitude-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Attitude</template>
            <template #subtitle>Examining mindset, motivation, and approach toward work and challenges.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-4">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to accept and follow procedures?
            </Label>
            <RadioGroup v-model="scores.followsProcedure">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-fp-` + option" :value="option.value" />
                <Label :for="index + `-fp-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> discretion in speech and actions?
            </Label>
            <RadioGroup v-model="scores.isDiscreteWithActions">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-idwa-` + option" :value="option.value" />
                <Label :for="index + `-idwa-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> demonstration of loyalty?
            </Label>
            <RadioGroup v-model="scores.exhibitsLoyalty">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-el-` + option" :value="option.value" />
                <Label :for="index + `-el-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> adaptability?
            </Label>
            <RadioGroup v-model="scores.adaptability">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-a-` + option" :value="option.value" />
                <Label :for="index + `-a-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to work well with administrative personnel and follow directions?
            </Label>
            <RadioGroup v-model="scores.worksWellWithAdmins">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-wwwoa-` + option" :value="option.value" />
                <Label :for="index + `-wwwoa-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Communication Skills-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Communication Skills</template>
            <template #subtitle>Assessing clarity, effectiveness, and professionalism in verbal and written interactions.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-6">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> effectiveness in presenting written ideas and information to others?
            </Label>
            <RadioGroup v-model="scores.communicationSkills">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-cs-` + option" :value="option.value" />
                <Label :for="index + `-cs-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Reliability and Accountability-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Reliability and Accountability</template>
            <template #subtitle>Evaluating consistency, responsibility, and trustworthiness in job performance.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-6">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to make decisions based on sound reasoning?
            </Label>
            <RadioGroup v-model="scores.reasoning">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-reasoning-` + option" :value="option.value" />
                <Label :for="index + `-reasoning-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> honesty and reliability in carrying out instructions?
            </Label>
            <RadioGroup v-model="scores.reliability">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-reliability-` + option" :value="option.value" />
                <Label :for="index + `-reliability-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> adherence to personal policies?
            </Label>
            <RadioGroup v-model="scores.observesPersonalPolicies">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-observesPersonalPolicies-` + option" :value="option.value" />
                <Label :for="index + `-observesPersonalPolicies-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> compliance with established working hours?
            </Label>
            <RadioGroup v-model="scores.compliesWithWorkingHours">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-compliesWithWorkingHours-` + option" :value="option.value" />
                <Label :for="index + `-compliesWithWorkingHours-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to meet deadlines and complete tasks as expected?
            </Label>
            <RadioGroup v-model="scores.meetsDeadlines">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-meetsDeadlines-` + option" :value="option.value" />
                <Label :for="index + `-meetsDeadlines-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--Proactiveness and Initiative-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>Proactiveness and Initiative</template>
            <template #subtitle>Assessing drive, resourcefulness, and willingness to take ownership of tasks.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-6">
            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> initiative as a self-starter?
            </Label>
            <RadioGroup v-model="scores.selfStarter">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-selfStarter-` + option" :value="option.value" />
                <Label :for="index + `-selfStarter-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to demonstrate originality and proactive creativity?
            </Label>
            <RadioGroup v-model="scores.decisionMaking">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-decisionMaking-` + option" :value="option.value" />
                <Label :for="index + `-decisionMaking-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>
              How would you rate <span class="font-semibold">{{ vaName }}'s</span> ability to proactively initiate action?
            </Label>
            <RadioGroup v-model="scores.initiatesAction">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-initiatesAction-` + option" :value="option.value" />
                <Label :for="index + `-initiatesAction-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <!--VA Evaluation and Feedback-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>VA Evaluation and Feedback</template>
            <template #subtitle>Providing performance scores and constructive comments for improvement.</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-6">
            <Label>Could you please provide your general feedback or comments on the Virtual Professional</Label>
            <Textarea v-model="generalVAComments" aria-label="General Feedback" placeholder="My Virtual Professional has demonstrated excellent performance..." class="min-h-32" />
          </div>
        </div>

        <!--MyOutDesk.com Service Review-->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-300 dark:border-white/30 pb-6">
          <SideHeader>
            <template #default>MyOutDesk.com Service Review</template>
            <template #subtitle>Assessing the quality and effectiveness of services provided by MyOutDesk.com</template>
          </SideHeader>
          <div class="md:col-span-2 space-y-6">
            <Label>How would you rate your overall experience with <span class="font-semibold">MyOutDesk</span>?</Label>
            <RadioGroup v-model="experienceWithMOD">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-experienceWithMOD-` + option" :value="option.value" />
                <Label :for="index + `-experienceWithMOD-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>How would you rate the company's responsiveness in addressing issues or concerns</Label>
            <RadioGroup v-model="companyFollowUpRating">
              <div v-for="(option, index) in qualities" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-companyFollowUpRating-` + option" :value="option.value" />
                <Label :for="index + `-companyFollowUpRating-` + option">{{ option.label }}</Label>
              </div>
            </RadioGroup>

            <Label>How would you rate the effectiveness of your Virtual Professional compared to a physical employee?</Label>
            <RadioGroup v-model="effectivenessOfVA">
              <div v-for="(option, index) in effectivenessLevels" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-effectivenessOfVA-` + option" :value="option" />
                <Label :for="index + `-effectivenessOfVA-` + option">{{ option }}</Label>
              </div>
            </RadioGroup>

            <Label>What percentage of savings do you estimate your Virtual Professional provides compared to employing a physical employee?</Label>
            <RadioGroup v-model="VASavings">
              <div v-for="(option, index) in effectivenessLevels2" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-VASavings-` + option" :value="option" />
                <Label :for="index + `-VASavings-` + option">{{ option }}</Label>
              </div>
            </RadioGroup>

            <Label>How many people do you know currently utilizing a Virtual Professional in their real estate business?</Label>
            <RadioGroup v-model="knowSomeoneInRealEstate">
              <div v-for="(option, index) in knownFriends" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-knowSomeoneInRealEstate-` + option" :value="option" />
                <Label :for="index + `-knowSomeoneInRealEstate-` + option">{{ option }}</Label>
              </div>
            </RadioGroup>

            <Label>Would you recommend <span class="font-semibold">MyOutDesk</span> to a friend or colleague?</Label>
            <RadioGroup v-model="wouldRecommend">
              <div v-for="(option, index) in yesNo" :key="index + `-` + option" class="flex items-center gap-2">
                <RadioGroupItem :id="index + `-wouldRecommend-` + option" :value="option" />
                <Label :for="index + `-wouldRecommend-` + option">{{ option }}</Label>
              </div>
            </RadioGroup>

            <Label>Do you have any additional feedback or comments about your experience with <span class="font-semibold">MyOutDesk</span>?</Label>
            <Textarea v-model="commentsAboutMOD" aria-label="Experience With MyOutDesk" placeholder="I had a really great experience..." class="min-h-32" />

            <div v-if="clientType !== 'Fixed Hours Billing'">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {{ bestScore }}
                  </CardTitle>
                  <CardDescription> MyOutDesk recommends a rate increase for the annual performance appraisal raise. </CardDescription>
                </CardHeader>
                <CardContent>
                  <Label>Score Range Recommended Raise</Label>
                  <div class="text-sm tracking-wider space-y-2">
                    <div class="flex">
                      <div class="w-24">3.00 to 3.25</div>
                      <div class="flex-1 border-b border-dashed text-right">$0.35</div>
                    </div>
                    <div class="flex">
                      <div class="w-24">3.26 to 3.50</div>
                      <div class="flex-1 border-b border-dashed text-right">$0.40</div>
                    </div>
                    <div class="flex">
                      <div class="w-24">3.51 to 3.70</div>
                      <div class="flex-1 border-b border-dashed text-right">$0.45</div>
                    </div>
                    <div class="flex">
                      <div class="w-24">3.76 to 4.00</div>
                      <div class="flex-1 border-b border-dashed text-right">$0.50</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <!--Evaluators Recommendation-->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          <SideHeader>
            <template #default>Evaluators Recommendation</template>
            <template #subtitle>Providing final insights and suggestions for future development and growth.</template>
          </SideHeader>

          <div class="md:col-span-2 space-y-4">
            <div v-if="clientType !== 'Fixed Hours Billing'" class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <InputLabel label="Salary increase per hour" :for-input="id" :required="true" />
                <Input v-model="approvedSalaryIncrease" placeholder="$0.50" class="max-w-24" type="number" />
              </div>
              <div class="md:col-span-2 space-y-4">
                <RadioGroup v-model="effectiveFrom" :default-value="4">
                  <div v-for="(option, index) in effectiveDates" :key="index + `-` + option" class="flex items-center gap-2">
                    <RadioGroupItem :id="index + `-effectiveFrom-` + option" :value="option" />
                    <Label :for="index + `-effectiveFrom-` + option">{{ option }}</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            <div v-else class="space-y-4">
              <p>
                A standard 4% annual increase is to be applied to your account effective <strong>{{ anniversaryDate }}</strong>
              </p>
              <p>Should you wish to give your VP a higher increase for an excellent year of service, you may do so here:</p>
              <Input v-model="desiredIncrease" type="number" placeholder="4" class="max-w-24" aria-label="rate increase" />
              <p v-if="parseFloat(desiredIncrease) >= 4">
                You have given your VP a score of <strong>{{ score }}</strong
                >, which means he/she has met the minimum requirements and expectations of a Virtual Professional.
              </p>
              <p v-if="parseFloat(desiredIncrease) >= 4">
                To reward your VP this past year, MyOutDesk will use a portion of the <strong class="dark:text-mod-orange-400">{{ desiredIncrease }}%</strong> to apply a $ <strong class="dark:text-mod-orange-400">{{ vaHourlyIncrease }}</strong> per hour on his/her pay.
              </p>
              <p v-if="parseFloat(desiredIncrease) >= 4">
                <span class="dark:text-mod-orange-400 font-medium">Your Current Monthly Rate:</span> <strong>$ {{ clientRate.toFixed(2) }}</strong>
              </p>
              <p v-if="parseFloat(desiredIncrease) >= 4">
                <span class="font-medium dark:text-mod-orange-400">Your New Monthly Rate:</span> <strong>$ {{ newMonthlyRate }}</strong>
              </p>
            </div>
            <div class="p-4 text-center">
              <Button size="lg" @click="submit">Submit</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
</style>
